import { RichTextInputWrapper } from '@framework/form/inputs/rich-text/RichTextInputWrapper.tsx';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { type IReference } from '../../references/IReference.ts';
import {
  type ComputeExpression,
  type ComputeExpressionValue,
} from '../ComputeExpression.ts';
import { computeExpressionToJsonContent } from './computeExpressionToJsonContent.tsx';
import { ComputeExpressionMenuBar } from './menu/ComputeExpressionMenuBar.tsx';
import { ComputeExpressionNode } from './nodes/ComputeExpressionNode.tsx';
import { ComputeExpressionReferenceNode } from './nodes/ComputeExpressionReferenceNode.tsx';
import { ComputeExpressionRootNode } from './nodes/ComputeExpressionRootNode.tsx';
import { computeExpressionReferenceSuggestion } from './reference-suggestions/computeExpressionReferenceSuggestion.tsx';

export interface IComputeExpressionBaseInputProps {
  disabled?: boolean;
  references?: IReference[];
  noFunctions?: boolean;
  onBlur?: (event: FocusEvent) => void;
  value: ComputeExpression | null | undefined;
  onChange: (value: ComputeExpression | null | undefined) => void;
}

export const ExpressionsEditorStyles = styled.div`
  .ProseMirror {
    /* font-size: 12px; */
    /* color: rgba(0, 0, 0, 0.88);
    font-size: 14px; */
    &.rte-input-lg {
      padding-top: 16px;
    }

    .compute-expression-node {
      margin-top: 2px;
      font-size: 16px;
    }
    pre {
      margin: 0;
    }

    span.compute-expression-reference {
      background-color: ${props => props.theme.colorPrimary};
      color: white;
      font-size: 14px;
      font-weight: bold;
      border-radius: 4px;
      padding: 2px 8px;
      line-height: 1.5;
    }
  }
`;

/**
 * JSON content per la gestione dell'editor per essere compatibile
 * con la `ComputeExpressionValue` registrata nell'elemento
 */
export interface ComputeExpressionJsonContent {
  type: 'root';
  content: [
    {
      type: 'node';
      attrs: {
        class: string;
      };
      content: ComputeExpressionValue;
    },
  ];
}

export function ComputeExpressionBaseInput(
  props: IComputeExpressionBaseInputProps,
) {
  const { references, value, onChange, onBlur, noFunctions } = props;

  // Trasformo l'Expression in un JSON content adatto a Tiptap
  const content = useMemo(() => computeExpressionToJsonContent(value), [value]);

  const editor = useEditor({
    extensions: [
      ComputeExpressionRootNode,
      ComputeExpressionNode,
      ComputeExpressionReferenceNode.configure({
        HTMLAttributes: {
          class: 'compute-expression-reference',
        },
        suggestion: computeExpressionReferenceSuggestion(references ?? []),
      }),
      Text,
    ],
    editable: props.disabled ? false : true,
    content,
    onBlur: ({ event }) => {
      onBlur?.(event);
    },
    onUpdate: ({ editor }) => {
      const content = editor.getJSON() as ComputeExpressionJsonContent;
      const text = editor.getText();
      onChange({ raw: text, value: content.content[0].content });
    },
    injectCSS: false,
    editorProps: {
      handlePaste: event => {
        return true;
      },
      attributes: {
        class: 'rich-text-input-editor',
      },
    },
  });

  return (
    <ExpressionsEditorStyles>
      <RichTextInputWrapper
        // size="large"
        menuBar={
          <ComputeExpressionMenuBar
            editor={editor}
            references={references ?? []}
            noFunctions={noFunctions}
          />
        }
      >
        <EditorContent editor={editor} />
      </RichTextInputWrapper>
    </ExpressionsEditorStyles>
  );
}
